// src/pages/MemberManagementPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMembersForManagement, updateMemberProfile } from '../api';
import NavBar from '../components/shared/NavBar';

const NAVY  = '#1B3A6B';
const GOLD  = '#C9A84C';
const RED   = '#c0392b';
const GREEN = '#1a6b3a';

const ROLES = ['member', 'house_rep', 'treasurer', 'executive'];
const HOUSES = [
  'Aggrey','Cadbury','Clark','Fraser','Guggisberg',
  'Gyamfi','House 17','Kingsley','Livingstone',
  'Lugard','Macarthy','Slessor','SOA','Stopford'
];

export default function MemberManagementPage() {
  const navigate = useNavigate();
  const { role } = useAuth();

  const [members,   setMembers]   = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  // Filters
  const [search,      setSearch]      = useState('');
  const [houseFilter, setHouseFilter] = useState('');
  const [roleFilter,  setRoleFilter]  = useState('');
  const [statusFilter,setStatusFilter]= useState('');

  // Editing
  const [editing,     setEditing]     = useState(null);
  const [editForm,    setEditForm]    = useState({});
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    if (role !== 'executive') {
      navigate('/unauthorized');
      return;
    }
    loadMembers();
  }, [role, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMembers() {
    setLoading(true);
    try {
      const result = await getMembersForManagement();
      setMembers(result.members || []);
      setFiltered(result.members || []);
    } catch (err) {
      setError('Could not load members: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  // Apply filters
  useEffect(() => {
    let list = [...members];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.name?.toLowerCase().includes(q) ||
        m.nickname?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.boardingHouse?.toLowerCase().includes(q)
      );
    }
    if (houseFilter) {
      list = list.filter(m => m.boardingHouse === houseFilter);
    }
    if (roleFilter) {
      list = list.filter(m => m.role === roleFilter);
    }
    if (statusFilter === 'active') {
      list = list.filter(m =>
        m.active !== false && m.active !== 'FALSE'
      );
    }
    if (statusFilter === 'inactive') {
      list = list.filter(m =>
        m.active === false || m.active === 'FALSE'
      );
    }
    if (statusFilter === 'no-email') {
      list = list.filter(m => !m.email);
    }

    setFiltered(list);
  }, [search, houseFilter, roleFilter, statusFilter, members]);

  const handleEdit = (member) => {
    setEditing(member.id);
    setEditForm({
      memberId:     member.id,
      role:         member.role || 'member',
      boardingHouse:member.boardingHouse || '',
      active:       member.active !== false &&
                    member.active !== 'FALSE'
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateMemberProfile({
        memberId:     editForm.memberId,
        role:         editForm.role,
        boardingHouse:editForm.boardingHouse,
        active:       editForm.active
      });

      // Update local state
      setMembers(prev => prev.map(m =>
        m.id === editForm.memberId
          ? { ...m,
              role:         editForm.role,
              boardingHouse:editForm.boardingHouse,
              active:       editForm.active
            }
          : m
      ));

      setSuccess(`${editForm.role} role saved successfully.`);
      setEditing(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };


  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getRoleBadge = (r) => {
    const map = {
      executive: { bg: '#fdecea', color: RED,    label: 'Executive' },
      treasurer: { bg: '#fff3cd', color: '#856404', label: 'Treasurer' },
      house_rep: { bg: '#d4edda', color: GREEN,  label: 'House Rep' },
      member:    { bg: '#d6eaf8', color: NAVY,   label: 'Member' }
    };
    return map[r] || map.member;
  };

  // Stats
  const stats = {
    total:     members.length,
    executives:members.filter(m => m.role === 'executive').length,
    houseReps: members.filter(m => m.role === 'house_rep').length,
    treasurers:members.filter(m => m.role === 'treasurer').length,
    noEmail:   members.filter(m => !m.email).length,
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
      <NavBar />

      <div style={{
        maxWidth: '1100px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff'
        }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
            👥 Member Management
          </h2>
          <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
            Manage roles, boarding houses and member status
          </p>
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: '12px', marginBottom: '20px'
        }}>
          {[
            { label: 'Total Members', value: stats.total,
              icon: '👥', bg: '#fff' },
            { label: 'Executives',    value: stats.executives,
              icon: '⭐', bg: '#fdecea' },
            { label: 'House Reps',    value: stats.houseReps,
              icon: '🏠', bg: '#d4edda' },
            { label: 'Treasurers',    value: stats.treasurers,
              icon: '💰', bg: '#fff3cd' },
            { label: 'No Email',      value: stats.noEmail,
              icon: '📵', bg: '#f0f4f8' },
          ].map(s => (
            <div key={s.label} style={{
              background: s.bg, borderRadius: '10px',
              padding: '16px', textAlign: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                {s.icon}
              </div>
              <div style={{
                fontSize: '22px', fontWeight: 'bold', color: NAVY
              }}>
                {s.value}
              </div>
              <div style={{
                fontSize: '11px', color: '#888',
                textTransform: 'uppercase', fontWeight: '600'
              }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Success / Error */}
        {success && (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: GREEN, fontSize: '14px',
            fontWeight: '600'
          }}>
            ✓ {success}
          </div>
        )}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: RED, fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Filters */}
        <div style={{
          background: '#fff', borderRadius: '12px',
          padding: '16px 20px', marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Search by name, nickname, email or house..."
            style={{
              width: '100%', padding: '10px 14px',
              border: '2px solid #e0e0e0', borderRadius: '8px',
              fontSize: '14px', outline: 'none',
              boxSizing: 'border-box', marginBottom: '12px'
            }}
          />
          <div style={{
            display: 'flex', gap: '10px', flexWrap: 'wrap'
          }}>
            {/* House filter */}
            <select
              value={houseFilter}
              onChange={e => setHouseFilter(e.target.value)}
              style={{
                padding: '8px 12px', border: '2px solid #e0e0e0',
                borderRadius: '8px', fontSize: '13px',
                outline: 'none', background: '#fff',
                cursor: 'pointer', flex: 1, minWidth: '140px'
              }}
            >
              <option value="">All Houses</option>
              {HOUSES.map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>

            {/* Role filter */}
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              style={{
                padding: '8px 12px', border: '2px solid #e0e0e0',
                borderRadius: '8px', fontSize: '13px',
                outline: 'none', background: '#fff',
                cursor: 'pointer', flex: 1, minWidth: '140px'
              }}
            >
              <option value="">All Roles</option>
              {ROLES.map(r => (
                <option key={r} value={r}>
                  {r === 'house_rep' ? 'House Rep' :
                   r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
            </select>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px', border: '2px solid #e0e0e0',
                borderRadius: '8px', fontSize: '13px',
                outline: 'none', background: '#fff',
                cursor: 'pointer', flex: 1, minWidth: '140px'
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="no-email">No Email</option>
            </select>

            {/* Clear */}
            {(search || houseFilter || roleFilter || statusFilter) && (
              <button
                onClick={() => {
                  setSearch('');
                  setHouseFilter('');
                  setRoleFilter('');
                  setStatusFilter('');
                }}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px', background: '#fff',
                  cursor: 'pointer', fontSize: '13px', color: '#666'
                }}
              >
                ✕ Clear
              </button>
            )}
          </div>

          <div style={{
            marginTop: '10px', fontSize: '13px', color: '#888'
          }}>
            Showing {filtered.length} of {members.length} members
          </div>
        </div>

        {/* Members Table */}
        {loading ? (
          <div style={{
            textAlign: 'center', padding: '60px',
            color: '#666', fontSize: '14px'
          }}>
            Loading members...
          </div>
        ) : (
          <div style={{
            background: '#fff', borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            overflow: 'hidden'
          }}>
            <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%', borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  {['Member', 'House', 'Role', 'Status',
                    'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '12px 16px', textAlign: 'left',
                      color: '#888', fontWeight: '600',
                      fontSize: '12px', textTransform: 'uppercase',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((member, i) => {
                  const badge    = getRoleBadge(member.role);
                  const isActive = member.active !== false &&
                                   member.active !== 'FALSE';
                  const isEditing = editing === member.id;

                  return (
                    <tr key={member.id} style={{
                      borderBottom: '1px solid #f0f0f0',
                      background: isEditing ? '#f0f4f8' : 'white'
                    }}>
                      {/* Member */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{
                          display: 'flex', alignItems: 'center',
                          gap: '10px'
                        }}>
                          <div style={{
                            width: '36px', height: '36px',
                            borderRadius: '50%', background: NAVY,
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: '13px',
                            fontWeight: 'bold', color: GOLD,
                            flexShrink: 0,
                            opacity: isActive ? 1 : 0.4
                          }}>
                            {getInitials(member.name)}
                          </div>
                          <div>
                            <div style={{
                              fontWeight: '600', color: '#333',
                              fontSize: '14px'
                            }}>
                              {member.name}
                              {member.nickname && (
                                <span style={{
                                  color: GOLD, fontStyle: 'italic',
                                  fontSize: '12px', marginLeft: '6px'
                                }}>
                                  "{member.nickname}"
                                </span>
                              )}
                            </div>
                            <div style={{
                              fontSize: '12px', color: '#888'
                            }}>
                              {member.email || (
                                <span style={{ color: RED }}>
                                  No email
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* House */}
                      <td style={{
                        padding: '12px 16px', color: '#555',
                        fontSize: '13px'
                      }}>
                        {isEditing ? (
                          <select
                            value={editForm.boardingHouse}
                            onChange={e => setEditForm(p => ({
                              ...p, boardingHouse: e.target.value
                            }))}
                            style={{
                              padding: '6px 10px',
                              border: '2px solid #e0e0e0',
                              borderRadius: '6px', fontSize: '13px',
                              outline: 'none', background: '#fff'
                            }}
                          >
                            <option value="">Select house</option>
                            {HOUSES.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        ) : (
                          member.boardingHouse || '—'
                        )}
                      </td>

                      {/* Role */}
                      <td style={{ padding: '12px 16px' }}>
                        {isEditing ? (
                          <select
                            value={editForm.role}
                            onChange={e => setEditForm(p => ({
                              ...p, role: e.target.value
                            }))}
                            style={{
                              padding: '6px 10px',
                              border: '2px solid #e0e0e0',
                              borderRadius: '6px', fontSize: '13px',
                              outline: 'none', background: '#fff'
                            }}
                          >
                            {ROLES.map(r => (
                              <option key={r} value={r}>
                                {r === 'house_rep' ? 'House Rep' :
                                 r.charAt(0).toUpperCase() + r.slice(1)}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span style={{
                            background: badge.bg, color: badge.color,
                            padding: '3px 8px', borderRadius: '10px',
                            fontSize: '12px', fontWeight: 'bold'
                          }}>
                            {badge.label}
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td style={{ padding: '12px 16px' }}>
                        {isEditing ? (
                          <select
                            value={editForm.active
                              ? 'active' : 'inactive'}
                            onChange={e => setEditForm(p => ({
                              ...p,
                              active: e.target.value === 'active'
                            }))}
                            style={{
                              padding: '6px 10px',
                              border: '2px solid #e0e0e0',
                              borderRadius: '6px', fontSize: '13px',
                              outline: 'none', background: '#fff'
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        ) : (
                          <span style={{
                            background: isActive
                              ? '#d4edda' : '#f0f0f0',
                            color: isActive ? GREEN : '#888',
                            padding: '3px 8px', borderRadius: '10px',
                            fontSize: '12px', fontWeight: 'bold'
                          }}>
                            {isActive ? '● Active' : '○ Inactive'}
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '12px 16px' }}>
                        {isEditing ? (
                          <div style={{
                            display: 'flex', gap: '8px'
                          }}>
                            <button
                              onClick={handleSave}
                              disabled={saving}
                              style={{
                                padding: '6px 14px',
                                background: saving ? '#ccc' : NAVY,
                                color: '#fff', border: 'none',
                                borderRadius: '6px',
                                cursor: saving
                                  ? 'not-allowed' : 'pointer',
                                fontSize: '12px', fontWeight: 'bold'
                              }}
                            >
                              {saving ? '...' : '✓ Save'}
                            </button>
                            <button
                              onClick={() => setEditing(null)}
                              style={{
                                padding: '6px 14px',
                                background: '#fff',
                                color: '#555',
                                border: '2px solid #e0e0e0',
                                borderRadius: '6px',
                                cursor: 'pointer', fontSize: '12px'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEdit(member)}
                            style={{
                              padding: '6px 14px',
                              background: '#f0f4f8',
                              color: NAVY,
                              border: `2px solid ${NAVY}`,
                              borderRadius: '6px',
                              cursor: 'pointer', fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            ✏️ Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {filtered.length === 0 && !loading && (
              <div style={{
                padding: '40px', textAlign: 'center',
                color: '#888', fontSize: '14px'
              }}>
                No members found matching your filters.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}